const BACKEND = process.env.NEXT_PUBLIC_BACKEND_HOST || '';

export const APP_SUPPORT_CHAT_URL     = `${BACKEND}ai/passis/chat`;
export const APP_SUPPORT_FEEDBACK_URL = `${BACKEND}ai/passis/feedback`;

export const buildAppSupportActionsUrl = (clubSlug: string) => `${BACKEND}ai/passis/${clubSlug}/actions`;
export const buildAppSupportApproveUrl = (clubSlug: string, actionId: number) =>
    `${BACKEND}ai/passis/${clubSlug}/actions/${actionId}/approve`;
export const buildAppSupportRejectUrl = (clubSlug: string, actionId: number) =>
    `${BACKEND}ai/passis/${clubSlug}/actions/${actionId}/reject`;
export const buildAppSupportAiSettingsUrl = (clubSlug: string) => `${BACKEND}ai/passis/${clubSlug}/ai-settings`;

export const APP_SUPPORT_EXAMPLE_QUESTIONS = [
    'Wo kann ich sehen, ob der Benutzer die E-Mail erhalten hat?',
    'Wo kann ich den QR-Code auf der Rechnung freischalten?',
    'Wie ändere ich Benachrichtigungen für Buchungen?',
    'Wo kann ich die Öffnungszeiten der Plätze ändern?',
    'Wo kann ich Benutzerrollen und Rechte verwalten?',
    'Wie erstelle ich Gutscheine oder Rabattcodes?',
    'Wo bearbeite ich E-Mail-Vorlagen für Mitglieder?',
] as const;

/** Icon key resolved to a FontAwesome icon in the view (keeps constants icon-free). */
export type AppSupportCapabilityIcon =
    | 'book'
    | 'compass'
    | 'users'
    | 'invoice'
    | 'reminder'
    | 'inbox'
    | 'course'
    | 'bookings'
    | 'spaces'
    | 'stats';

export type AppSupportCapability = {
    icon: AppSupportCapabilityIcon;
    title: string;
    description: string;
    /** Prefilled into the input when the capability is picked (not auto-sent). */
    prompt: string;
    /** Requires the club.settings permission (admin-only tools). */
    admin?: boolean;
};

/** Plain-language note explaining that write actions are staged, not executed directly. */
export const APP_SUPPORT_CAPABILITY_APPROVAL_HINT =
    'Der Assistent legt nichts an und löscht nichts. Erinnerungen und Mitteilungen werden nur vorbereitet – du gibst sie danach mit einem Klick frei.';

/**
 * Human-readable summary of what the assistant can do. Grouped loosely by the
 * underlying tool set; admin capabilities are hidden for non-admins.
 */
export const APP_SUPPORT_CAPABILITIES: AppSupportCapability[] = [
    {
        icon: 'book',
        title: 'Anleitungen & Hilfe',
        description: 'Erklärt Abläufe und beantwortet Wie-funktioniert-Fragen.',
        prompt: 'Wie erstelle ich Gutscheine oder Rabattcodes?'
    },
    {
        icon: 'compass',
        title: 'Einstellungen finden',
        description: 'Zeigt dir, wo du eine Einstellung im System vornimmst.',
        prompt: 'Wo kann ich die Öffnungszeiten der Plätze ändern?'
    },
    {
        icon: 'users',
        title: 'Mitglieder ansehen',
        description: 'Listet Mitglieder und ihre Kategorien auf.',
        prompt: 'Zeige mir alle Mitglieder. Aus der Kategorie ...' ,
        admin: true
    },
    {
        icon: 'invoice',
        title: 'Rechnungen prüfen',
        description: 'Findet offene und bezahlte Rechnungen.',
        prompt: 'Zeige mir alle nicht bezahlten Rechnungen.',
        admin: true
    },
    {
        icon: 'reminder',
        title: 'Zahlungserinnerung vorbereiten',
        description: 'Bereitet Erinnerungen an offene Beiträge zur Freigabe vor.',
        prompt: 'Erstelle eine Zahlungserinnerung für alle offene Rechnungen aus letzter Woche.',
        admin: true
    },
    {
        icon: 'inbox',
        title: 'Anfragen zusammenfassen',
        description: 'Fasst Mitgliedsanträge und Kontaktanfragen zusammen.',
        prompt: 'Fasse die Anfragen von heute für mich zusammen.',
        admin: true
    },
    {
        icon: 'course',
        title: 'Kurs-Beliebtheit',
        description: 'Zeigt Auslastung und beliebteste Kurse im Zeitraum.',
        prompt: 'Welche Kurse waren zuletzt am beliebtesten?',
        admin: true
    },
    {
        icon: 'bookings',
        title: 'Buchungen & Belegungen',
        description: 'Listet aktuelle Buchungen und Platzbelegungen auf.',
        prompt: 'Zeige mir die aktuellen Buchungen.',
        admin: true
    },
    {
        icon: 'stats',
        title: 'Statistiken',
        description: 'Liefert Kennzahlen zu Mitgliedern, Buchungen und Umsatz.',
        prompt: 'Zeige mir die wichtigsten Vereinsstatistiken.',
        admin: true
    }
];

/** Club-level AI consent state returned by the ai-settings endpoint. */
export type AppSupportAiSettings = {
    enabled: boolean;
    /** True when the current user is a super admin (Vereinsleitung) who may toggle it. */
    can_manage: boolean;
    enabled_at?: string | null;
    enabled_by?: { id: number; name?: string } | null;
};

export const APP_SUPPORT_PRIVACY_NOTICE = {
    title: 'Vereinsdaten-Funktionen für deinen Verein',
    body: 'Der Assistent kann dir mit Vereinsdaten helfen – Mitglieder, Rechnungen und Buchungen. E-Mails und Änderungen werden erst nach deiner Freigabe ausgeführt. Diese Einstellung gilt für den ganzen Verein.',
    managerHint: 'Als Vereinsleitung kannst du die Funktionen für alle Mitarbeitenden freischalten.',
    memberHint: 'Nur die Vereinsleitung kann diese Funktionen für den Verein freischalten.',
    enable: 'Für Verein aktivieren',
    disable: 'Deaktivieren',
    activeLabel: 'Vereinsdaten-Funktionen sind für den Verein aktiv',
    inactiveLabel: 'Vereinsdaten-Funktionen sind deaktiviert'
} as const;
